import dayjs from 'dayjs';
import { CapacityRange } from '../models/serializable/capacity-range.js';
import { CalendarDaySlot } from '../models/serializable/calendar-state/calendar-day-slot.js';
import { CalendarDayState } from '../models/serializable/calendar-state/calendar-day-state.js';
import { DailyCalendarSnapshot } from '../models/serializable/calendar-state/daily-calendar-snapshot.js';
import { GroupReservationCalendarSlot } from '../models/serializable/calendar-state/group-reservation-calendar-slot.js';
import { OpenReservationCalendarSlot } from '../models/serializable/calendar-state/open-reservation-calendar-slot.js';
import { SingleEntryCalendarSlot } from '../models/serializable/calendar-state/single-entry-calendar-slot.js';
import { TimeOfDay } from '../models/serializable/time-of-day.js';
import { DatabaseIdGenerator } from '../utility/database-id-generator.js';
import { SerializationException } from '../exceptions/serialization-exception.js';
import { BinaryReader } from './binary-reader.js';
import { BinaryWriter } from './binary-writer.js';

export type DailyCalendarSnapshotBuilder = (
  calendars: Map<string, CalendarDayState>,
  dayOfMonth: number
) => DailyCalendarSnapshot;

export class MonthlyCalendarStateSerializer {
  private static readonly SINGLE_ENTRY_SLOT_ID: number = 1;
  private static readonly GROUP_ENTRY_SLOT_ID: number = 2;
  private static readonly OPEN_ENTRY_SLOT_ID: number = 3;

  static serialize(calendars: Map<number, DailyCalendarSnapshot>): Uint8Array {
    return this.asBytes(calendars);
  }

  static deserialize(bytes: Uint8Array, builder: DailyCalendarSnapshotBuilder): Map<number, DailyCalendarSnapshot> {
    const reader = BinaryReader.fromBytes(bytes);
    const format = reader.readByte();

    switch(format) {
      case 1:
        return this.fromBytes(reader, builder);
    }

    throw new SerializationException(`Calendar state cannot be deserialized because format ${format} is unknown`);
  }

  private static asBytes(calendars: Map<number, DailyCalendarSnapshot>): Uint8Array {
    const writer = new BinaryWriter();
    const format = 1;

    writer.writeByte(format);
    writer.writeByte(calendars.size);

    for(const calendarSnapshot of calendars.values()) {
      this.writeDailyCalendarSnapshot(writer, calendarSnapshot);
    }

    return writer.toBytes();
  }

  static writeDailyCalendarSnapshot(writer: BinaryWriter, calendarSnapshot: DailyCalendarSnapshot): void {
    writer.writeByte(calendarSnapshot.dayOfMonth);
    writer.writeByte(calendarSnapshot.calendars.size);

    for(const calendarState of calendarSnapshot.calendars.values()) {
      this.writeCalendarState(writer, calendarState);
    }
  }

  static writeCalendarState(writer: BinaryWriter, calendarState: CalendarDayState): void {
    if(calendarState.uniqueId.length > DatabaseIdGenerator.kAuthIdLength) {
      throw new SerializationException('Calendar day state ID is not valid');
    }

    this.writeUniqueId(writer, calendarState.uniqueId, DatabaseIdGenerator.kAuthIdLength);

    const slots = calendarState.slots;
    writer.writeInt32(slots.length);

    for(let i = 0; i < slots.length; i++) {
      const slot = slots[i];
      if(slot.entryId.length > DatabaseIdGenerator.kAutoIdLength) {
        throw new SerializationException('Calendar entry ID is not valid');
      }

      if(slot instanceof SingleEntryCalendarSlot) {
        this.writeSingleEntryCalendarSlot(writer, slot);
      }
      else if(slot instanceof GroupReservationCalendarSlot) {
        this.writeGroupCalendarSlot(writer, slot);
      }
      else if(slot instanceof OpenReservationCalendarSlot) {
        this.writeOpenCalendarSlot(writer, slot);
      }
      else {
        throw new SerializationException(`Unsupported staff calendar slot: ${slot.constructor.name}`);
      }
    }
  }

  static writeSingleEntryCalendarSlot(writer: BinaryWriter, slot: SingleEntryCalendarSlot) {
    writer.writeByte(this.SINGLE_ENTRY_SLOT_ID);

    this.writeUniqueId(writer, slot.entryId, DatabaseIdGenerator.kAutoIdLength);
    writer.writeInt32(slot.timeOfDay.inMinutes);
    writer.writeInt32(slot.duration.asMilliseconds());
  }

  static writeGroupCalendarSlot(writer: BinaryWriter, slot: GroupReservationCalendarSlot) {
    writer.writeByte(this.GROUP_ENTRY_SLOT_ID);

    this.writeUniqueId(writer, slot.entryId, DatabaseIdGenerator.kAutoIdLength);
    this.writeUniqueId(writer, slot.serviceId, DatabaseIdGenerator.kAutoIdLength);

    writer.writeInt32(slot.timeOfDay.inMinutes);
    writer.writeInt32(slot.duration.asMilliseconds());
    writer.writeInt32(slot.capacity.from);
    writer.writeInt32(slot.capacity.to);
    writer.writeInt32(slot.occupancy);
  }

  static writeOpenCalendarSlot(writer: BinaryWriter, slot: OpenReservationCalendarSlot) {
    writer.writeByte(this.OPEN_ENTRY_SLOT_ID);

    this.writeUniqueId(writer, slot.entryId, DatabaseIdGenerator.kAutoIdLength);
    this.writeUniqueId(writer, slot.serviceId, DatabaseIdGenerator.kAutoIdLength);

    writer.writeInt32(slot.timeOfDay.inMinutes);
    writer.writeInt32(slot.duration.asMilliseconds());
    writer.writeBool(slot.isOpen);
  }

  static fromBytes(reader: BinaryReader, builder: DailyCalendarSnapshotBuilder): Map<number, DailyCalendarSnapshot> {
    const calendars = new Map<number, DailyCalendarSnapshot>();
    const count = reader.readByte();

    for(let i = 0; i < count; i++) {
      const calendarSnapshot = this.readCalendarSnapshot(reader, builder);
      calendars.set(calendarSnapshot.dayOfMonth, calendarSnapshot);
    }

    return calendars;
  }

  static readCalendarSnapshot(reader: BinaryReader, builder: DailyCalendarSnapshotBuilder): DailyCalendarSnapshot {
    const calendars = new Map<string, CalendarDayState>();
    const dayOfMonth = reader.readByte();
    const stateCount = reader.readByte();

    for(let i = 0; i < stateCount; i++) {
      const calendarState = this.readCalendarState(reader);
      calendars.set(calendarState.uniqueId, calendarState);
    }

    return builder(calendars, dayOfMonth);
  }

  static readCalendarState(reader: BinaryReader): CalendarDayState {
    const uniqueId = this.readUniqueId(reader, DatabaseIdGenerator.kAuthIdLength);
    const slots: CalendarDaySlot[] = [];
    const numberOfSlots = reader.readInt32();

    for(let i = 0; i < numberOfSlots; i++) {
      const slotType = reader.readByte();

      if(slotType === this.SINGLE_ENTRY_SLOT_ID) {
        slots.push(this.readSingleEntryCalendarSlot(reader));
      }
      else if(slotType === this.GROUP_ENTRY_SLOT_ID) {
        slots.push(this.readGroupCalendarSlot(reader));
      }
      else if(slotType === this.OPEN_ENTRY_SLOT_ID) {
        slots.push(this.readOpenCalendarSlot(reader));
      }
      else {
        throw new SerializationException(`Unknown staff calendar slot type: ${slotType}`);
      }
    }

    return new CalendarDayState(uniqueId, slots);
  }

  static readSingleEntryCalendarSlot(reader: BinaryReader): CalendarDaySlot {
    const entryId = this.readUniqueId(reader, DatabaseIdGenerator.kAutoIdLength);
    const timeOfDay = TimeOfDay.fromMinutes(reader.readInt32());
    const duration = dayjs.duration(reader.readInt32());

    return new SingleEntryCalendarSlot(entryId, timeOfDay, duration);
  }

  static readGroupCalendarSlot(reader: BinaryReader): CalendarDaySlot {
    const entryId = this.readUniqueId(reader, DatabaseIdGenerator.kAutoIdLength);
    const serviceId = this.readUniqueId(reader, DatabaseIdGenerator.kAutoIdLength);

    const timeOfDay = TimeOfDay.fromMinutes(reader.readInt32());
    const duration = dayjs.duration(reader.readInt32());
    const capacity = CapacityRange.between(reader.readInt32(), reader.readInt32());
    const occupancy = reader.readInt32();

    return new GroupReservationCalendarSlot(entryId, timeOfDay, duration, serviceId, capacity, occupancy);
  }

  static readOpenCalendarSlot(reader: BinaryReader): CalendarDaySlot {
    const entryId = this.readUniqueId(reader, DatabaseIdGenerator.kAutoIdLength);
    const serviceId = this.readUniqueId(reader, DatabaseIdGenerator.kAutoIdLength);

    const timeOfDay = TimeOfDay.fromMinutes(reader.readInt32());
    const duration = dayjs.duration(reader.readInt32());
    const isOpen = reader.readBool();

    return new OpenReservationCalendarSlot(entryId, timeOfDay, duration, serviceId, isOpen);
  }

  static writeUniqueId(writer: BinaryWriter, uniqueId: string, maxLength: number) {
    let length = 0;

    for(let i = 0; i < uniqueId.length; i++) {
      writer.writeByte(uniqueId.charCodeAt(i));
      length++;
    }

    while(length < maxLength) {
      writer.writeByte(0);
      length++;
    }
  }

  static readUniqueId(reader: BinaryReader, maxLength: number): string {
    const charCodes: number[] = [];
    for(let i = 0; i < maxLength; i++) {
      const code = reader.readByte();
      if(code > 0) {
        charCodes.push(code);
      }
    }

    return String.fromCharCode(...charCodes);
  }
}
